import type { WrittenPage } from '../../sections'
import { STACK_GROUPS_TR } from '../../stack'

/**
 * Kurumsal Yapay Zeka Çözümleri — FDE ekipleri, Agent Hub ve Agent Core.
 *
 * Agent Core, Agent Hub ve Agentic Platform ürün adı olarak İngilizce kalıyor:
 * İngilizce sayfadaki CHECK notu bunların nihai dış adları olup olmadığını
 * soruyor, ve bir ürün adını yalnızca bir dilde çevirmek en kötü ihtimal olur.
 * MCP, A2A, API, RPA, GRC ve FinOps de aynı sebeple çevrilmedi.
 */
export const enterpriseAiSolutions: WrittenPage = {
  navLabel: 'Kurumsal Yapay Zeka Çözümleri',
  title: 'Kurumsal Yapay Zeka Çözümleri: Agent Hub, Agent Core',
  description:
    'SAP, Oracle, IFS, Workday ve Salesforce üzerinde çalışan kurumlar için ajan tabanlı yapay zeka. Bir kez kurulan Agent Core, kullanım senaryosu başına kurulan Agent Hub ve Forward Deployed Engineer ekipleri.',
  sections: [
    {
      kind: 'hero',
      headline:'Süreç uygulamalarınızda duruyor. Ajanlarınızın ihtiyacı olan şey yetki.',
      sub: 'Ajan tabanlı yapay zekayı kurumsal sınıf altyapı üzerinde ölçekte devreye alın; yetki sınırını bir satıcı değil, iş tarafı belirlesin.',
      photo: {
        src: '/photo/overhead-table-charts.jpg',
        alt: 'Sahada çalışan mühendisler ve iş tarafı, tek bir masa etrafında tepeden görünümde; laptoplar ve kâğıt üzerinde mimari şemalar.',
      },
      ctas: [{ label: 'Bize Ulaşın', href: '#contact' }],
    },
    {
      kind: 'stack',
      groups: STACK_GROUPS_TR,
    },
    {
      kind: 'prose',
      heading: 'Neden ajan tabanlı, ve neden şimdi.',
      paragraphs: [
        'Sohbet botu yanıtlar. Ajan eyler. Yazılım kritik sistemlerinizin içinde eyleme geçebildiği anda zor soru teknik olmaktan çıkar, yönetişim sorusuna dönüşür. Neye izinli. Sonucun sahibi kim. Kararı hangi noktada bir insan verir. Ve bunların hepsini sonradan nasıl kanıtlarsınız.',
        'Bugün pek çok kurumun rafında umut veren demolar var, üretimde ise neredeyse hiçbir şey. Aradaki fark nadiren model kalitesidir. Bir ajanı işe koşacak, yönetilen bir yerin yokluğudur.',
      ],
      pullquote: 'Pilot kolaydır, çünkü ortada hiçbir şey yoktur. Ölçek zordur, çünkü her şey ortadadır.',
    },
    {
      kind: 'features',
      heading: 'Kurumsal Yapay Zeka Çözümleri Mimarisi',
      columns: 2,
      textSize: 'lg',
      items: [
        {
          title: 'Agent Core',
          note: 'Bir kez kurulur, paylaşılır.',
          body: 'Kurumsal sınıf ajan tabanlı altyapı. Sistemin neyi yapabildiğidir. Harness ve ajan çalışma zamanı: ajan tabanlı döngü, bağlam derleme, izolasyon, kalıcılık, orkestrasyon. Bilgi katmanı: veri ve köken, semantik ve ontoloji, ajan belleği. Entegrasyon ve eylem geçidi: kayıt defteri, araç aracısı, MCP, A2A, API\'ler, RPA. Çıkarım ve model yönlendirme: model portföyü, geçit, önbellek, toplu işleme, yedekleme.',
        },
        {
          title: 'Agent Hub',
          note: 'Kullanım senaryosu başına kurulur.',
          body: 'Ajan tasarım ve çalıştırma ortamı. İş tarafının sisteme neyi yapma yetkisi verdiğidir. Politika ve yetki sınırı: ne deterministik kod kalır, ne model muhakemesine devredilir. Ajanlar: roller, yetkinlikler, araç izinleri, alt ajan topolojisi, devirler. İnsan yetkisi ve etkileşim: devredilmiş karar hakları, onay kapıları, eskalasyon yolları, denetim izi.',
        },
      ],
      footnote:
        'Agent Core\'unuzu bir kez kurarız. Agent Hub, iş tarafının ona neyi emanet edeceğine karar verdiği yerdir.',
    },
    {
      kind: 'features',
      heading: 'Enterprise AI Foundation',
      columns: 4,
      items: [
        {
          title: 'Güvence ve GRC',
          body: 'Risk komitesinin kabul edeceği kontroller: iddia edilen değil, kanıtlanan.',
        },
        {
          title: 'Kimlik ve yetkilendirme',
          body: 'Ajanlar da estate\'inizdeki her aktör gibi kimlik ve kapsamı belirli izinler alır.',
        },
        {
          title: 'Değerlendirme ve gözlemlenebilirlik',
          body: 'Ajanın ne yaptığını, neden yaptığını ve iyileşip iyileşmediğini görebilirsiniz.',
        },
        {
          title: 'FinOps ve birim ekonomisi',
          body: 'Token başına maliyet değil, sonuç başına maliyet. Bir CFO\'nun sizi bağlayabileceği sayı.',
        },
      ],
    },
    {
      kind: 'list',
      heading: 'Forward Deployed Engineers',
      intro:
        'FDE\'ler iş insanlarınızın ve mühendislerinizin yanına oturur; süreçlerinizin ve kod tabanınızın içindedir. Agent Core\'u estate\'inize, Agent Hub\'ı iş akışlarınıza taşırlar; arkalarında modelleri, bağlamı ve değerlendirmeleri ayarlayan AI mühendisleriyle birlikte.',
      variant: 'bullet',
      items: [
        'Sonucu bir kullanım senaryosu başlığı değil, ölçülebilir bir değer birimi olarak tanımlayın',
        'İlk canlı iş akışını taşıyacak kadar Agent Core\'u ayağa kaldırın',
        'Bir insan onay kapısının arkasında tek bir ajanı üretime alın',
        'Sınırı genişletmeden önce değerlendirme düzeneğini kurun',
        'Sonraki senaryolara, zaten var olan altyapı üzerinde ölçeklenin',
      ],
      cta: { label: 'Bize Ulaşın', href: '#contact' },
    },
    {
      kind: 'features',
      heading: 'Her şey tek bir yönetilen yüzeyden bağlanır.',
      columns: 3,
      items: [
        {
          title: 'Kritik sistemler',
          body: 'SAP, Oracle, IFS, Workday, Salesforce, Databricks, Snowflake.',
        },
        {
          title: 'Ekosistem',
          body: 'Müşteriler, tedarikçiler, lojistik iş ortakları, banka entegrasyonları, regülatör entegrasyonları, üçüncü taraflar.',
        },
        {
          title: 'Operasyon',
          body: 'Fabrikalar, depolar, mağaza ve şubeler, lojistik ve bunların arasında akan süreçler.',
        },
      ],
      footnote:
        'Entegrasyon ve eylem geçidi, yönetilen araç yüzeyi ve gerçek güvenlik çeperidir. Ajanları ERP\'nize kadar genişletirken riskinizi genişletmemenizin sebebi odur.',
    },
    // Kapanıştaki siyah CTA bandı kaldırıldı (2026-08-30) — sayfanın altındaki
    // form zaten orada.
  ],
}
